/* mediaUpload.js — direct-from-device image upload helper for the
 * Distribution form (and any future admin surface that wants both a
 * Media Library picker AND a device upload).
 *
 * Goal: never create a second storage system. Every file selected on
 * the device gets POSTed to /api/admin/media — the same endpoint
 * MediaLibraryPanel uses — so uploaded assets show up in the central
 * Media Library and can be reused later.
 *
 * Also does lightweight client-side compression (only when the source
 * is bigger than ~2 MB) so admins on slow phone connections aren't
 * shipping 8 MP originals over the wire. The backend re-generates its
 * own responsive variants regardless, so we only optimize for
 * upload-speed, not final quality.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIA_UPLOAD_URL = `${BACKEND_URL}/api/admin/media`;
const MEDIA_SERVE_URL = `${BACKEND_URL}/api/media/serve`;

export const ACCEPTED_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const ACCEPTED_ACCEPT_ATTR = 'image/jpeg,image/jpg,image/png,image/webp';
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // matches backend MAX_BYTES

// Public: URL a saved media item is served from at "desktop" size
// (the largest real variant — 1920px WebP). Historic name "large" is
// still accepted via a backend alias for backward compat with URLs
// that were saved into distribution / site-image records before this
// fix landed, but every new URL built here should use a valid
// variant name from the outset.
export const servedUrl = (mediaId, size = 'desktop') =>
  `${MEDIA_SERVE_URL}/${mediaId}/${size}`;

const COMPRESS_THRESHOLD = 2 * 1024 * 1024; // 2 MB
const MAX_DIMENSION = 2000;                 // longest edge after compress
const WEBP_QUALITY = 0.85;

/**
 * Compress an image File in the browser via <canvas>. If the source is
 * already small, or is a PNG (transparency) under threshold, we skip
 * compression to preserve the original bytes.
 *
 * Returns a Promise<File>. The returned File keeps the original name
 * (with .webp appended when we recode) so the backend picks up the
 * right extension.
 */
export const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    if (file.size <= COMPRESS_THRESHOLD) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => resolve(file);
      img.onload = () => {
        const { width, height } = img;
        const longest = Math.max(width, height);
        const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
        const targetW = Math.round(width * scale);
        const targetH = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }
        ctx.drawImage(img, 0, 0, targetW, targetH);
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            // Only accept compressed result if it's actually smaller.
            if (blob.size >= file.size) { resolve(file); return; }
            const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
            const compressed = new File([blob], `${baseName}.webp`, {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            resolve(compressed);
          },
          'image/webp',
          WEBP_QUALITY,
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

/**
 * Validate a File before we bother compressing/uploading. Returns a
 * string error message or null if the file is OK.
 */
export const validateImageFile = (file) => {
  if (!file) return 'No file selected.';
  const mimeOk = ACCEPTED_MIME.includes((file.type || '').toLowerCase());
  const nameOk = /\.(jpe?g|png|webp)$/i.test(file.name || '');
  if (!mimeOk && !nameOk) {
    return 'Only JPG, PNG, or WebP images are supported.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File is too large (max ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB).`;
  }
  return null;
};

/**
 * Upload one or more compressed Files to the Media Library. Uses XHR
 * so we can surface percent progress to the UI. Resolves with an array
 * of served URLs (`{ url, id }`) matching the order of `uploaded` in
 * the backend response.
 *
 * `onProgress(pct)` fires with a 0-100 integer as bytes are sent.
 */
export const uploadFilesToMediaLibrary = (files, onProgress) => {
  return new Promise((resolve, reject) => {
    if (!files || files.length === 0) {
      resolve([]);
      return;
    }
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', MEDIA_UPLOAD_URL, true);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.onload = () => {
      let payload = null;
      try { payload = JSON.parse(xhr.responseText || '{}'); }
      catch (_) { payload = null; }
      if (xhr.status < 200 || xhr.status >= 300) {
        const detail = (payload && (payload.detail || payload.message))
                    || `Upload failed (HTTP ${xhr.status}).`;
        reject(new Error(String(detail)));
        return;
      }
      const uploaded = (payload && payload.uploaded) || [];
      const errors = (payload && payload.errors) || [];
      if (uploaded.length === 0 && errors.length > 0) {
        reject(new Error(errors[0].error || 'Upload failed.'));
        return;
      }
      const out = uploaded.map((m) => ({ id: m.id, url: servedUrl(m.id, 'large') }));
      // Surface partial failures without blocking the successful ones —
      // caller can decide whether to toast.
      if (errors.length > 0) {
        out.partialErrors = errors.map((e) => e.error || 'Upload failed.');
      }
      resolve(out);
    };
    xhr.send(fd);
  });
};

/**
 * Convenience wrapper: validate + compress + upload a list of Files.
 * Returns the same shape as `uploadFilesToMediaLibrary`.
 */
export const processAndUpload = async (files, onProgress) => {
  const list = Array.from(files || []);
  if (list.length === 0) return [];

  for (const f of list) {
    const err = validateImageFile(f);
    if (err) throw new Error(err);
  }

  // Compress in parallel — canvas work is per-image and browser
  // schedulers handle it fine; sequential would be slow on multi-photo
  // uploads.
  const compressed = await Promise.all(list.map(compressImage));
  return uploadFilesToMediaLibrary(compressed, onProgress);
};

// Also re-export axios so future admin components can piggyback if
// they need to check the Media Library via GET after uploading.
export { axios };
